// #region Imports
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import UndoIcon from "@mui/icons-material/Undo";
import UploadIcon from "@mui/icons-material/Upload";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { keyframes, useMediaQuery } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import { green, lightBlue } from "@mui/material/colors";
import { alpha, styled, useTheme } from "@mui/material/styles";
import { skipToken } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { Change, diffLines } from "diff";
import {
  createMRTColumnHelper,
  type MRT_Row,
  type MRT_TableInstance,
  type MRT_TableOptions,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from "react";
import { defaultStyles, FileIcon } from "react-file-icon";
import {
  getApiErrorData,
  getApiErrorMessage,
  getApiErrorStatus,
  getErrorMessageFromUnknown,
} from "_shared/_errors/api-error-message";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { enumKeyOf } from "_shared/_utilities/enum-utils";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { useResizableColumns } from "_shared/_views/tables/table-column-widths";
import Guid from "_shared/guids/guid";
import { createBulkDocumentRequests } from "documents/_api/commands/documents-form-api-request";
import { useCreateDocumentsMutation } from "documents/_api/document-api";
import { getDocumentUploadSnackbar, summarizeDocumentUploadResults } from "documents/_api/document-upload-results";
import { knownLegacyDocumentErrors } from "documents/_errors/document-api-errors";
import { DocumentFileType } from "documents/document-file-type";
import DocumentTopic from "documents/topics/document-topic";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import Individual from "marketplace/individuals/individual";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { proposalCollectionItemRevertValue } from "work/proposals/negotiation/proposal-collection-item-revert";
import ProposalFieldHistoryButton from "work/proposals/negotiation/proposal-field-history-button";
import ProposalFieldTimelineDropdown, {
  type ProposalFieldTimelineRevertEntry,
} from "work/proposals/negotiation/proposal-field-timeline-dropdown";
import ProposalField from "work/proposals/proposal-fields/proposal-field";
import ProposalInfoTooltip from "work/proposals/_views/proposal-info-tooltip";
import ProposalAddedBadge from "work/proposals/_views/_shared/proposal-added-badge";
import ProposalDeleteActionButton from "work/proposals/_views/_shared/proposal-delete-action-button";
import ProposalInfoLink from "work/proposals/_views/_shared/proposal-info-link";
import ProposalRedlineSubtext from "work/proposals/_views/_shared/proposal-redline-subtext";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import { ReviewerEditLockIcon } from "work/proposals/reviewers/_views/reviewer-edit-lock-feedback";
import { rowActionsColumnWidth } from "work/work-agreements/contracts/_views/document-collection-columns";
import {
  ADDED_BY_COLUMN_HEADER,
  ADDED_BY_COLUMN_ID,
  ADDED_BY_COLUMN_WIDTH,
  ADDED_COLUMN_HEADER,
  ADDED_COLUMN_ID,
  ADDED_COLUMN_WIDTH,
  DOCUMENT_COLUMN_HEADER,
  DOCUMENT_COLUMN_ID,
  DOCUMENT_COLUMN_WIDTH,
  DOCUMENT_CONTENT_COLUMN_IDS,
  DocumentCollectionActionButton,
  DocumentCollectionActions,
  DocumentCollectionHeader,
  DocumentCollectionHeading,
  DocumentCollectionNoRows,
  DocumentCollectionRoot,
  DocumentCollectionToolbar,
  DocumentLockCellContent,
  DocumentRowActions,
  documentCollectionBodyCellStyles,
  documentCollectionHeadCellStyles,
  documentCollectionTableOptions,
  documentCreatedLabel,
  documentTypeLabel,
  FeeScheduleTableCellText,
  LOCKED_COLUMN_ID,
  LOCKED_COLUMN_WIDTH,
  TYPE_COLUMN_HEADER,
  TYPE_COLUMN_ID,
  TYPE_COLUMN_WIDTH,
  UNKNOWN_VALUE,
  useDocumentCollectionColumnFit,
} from "work/work-agreements/contracts/_views/document-collection-table";
import WorkDocument from "work/work-agreements/contracts/work-document";
import { WorkDocumentType } from "work/work-agreements/contracts/work-document-type";
// #endregion

// #region Column identity
/** The columns a redline decision is drawn across, in the order they are read. */
const REDLINED_COLUMN_IDS = DOCUMENT_CONTENT_COLUMN_IDS;

/**
 * What each of the three collections this one view serves calls itself, and says about itself.
 *
 * The three used to be three near-identical blocks of JSX differing only in these strings, gated on a
 * mixture of `accountType` and `documentsRedline.field`; a Client-owned list whose documents were
 * Conflicts satisfied both gates and drew every row twice. Naming them once, off the single prop that
 * says which collection this is, is what stops that.
 */
const DOCUMENT_COLLECTIONS: Readonly<
  Record<WorkDocumentType, { title: string; about: string; uploadLabel: string }>
> = {
  [WorkDocumentType.Conflicts]: {
    title: "Conflict Documents",
    about:
      "Conflicts document(s) establish potential conflicts checks required by participating clients/vendors",
    uploadLabel: "Upload Conflicts Check Document",
  },
  [WorkDocumentType.ClientPolicy]: {
    title: "Client Policies",
    about: "Client policies establish the terms and conditions that the client requires for the vendor to adhere to",
    uploadLabel: "Upload Client Policy Document",
  },
  [WorkDocumentType.VendorPolicy]: {
    title: "Vendor Policies",
    about: "Vendor policies establish the terms and conditions that the vendor requires for the client to adhere to",
    uploadLabel: "Upload Vendor Policy Document",
  },
};

/**
 * The field whose history the collection's own header offers.
 *
 * Only the policies have one. Conflicts is one list shared by both parties and its header has never
 * carried a history control; #4953 took the per-collection Accept All / Reject All / Undo All off all
 * three, so this is the only collection-wide control left.
 */
const COLLECTION_HISTORY_FIELDS: Readonly<Partial<Record<WorkDocumentType, ProposalField>>> = {
  [WorkDocumentType.ClientPolicy]: ProposalField.ClientPolicies,
  [WorkDocumentType.VendorPolicy]: ProposalField.VendorPolicies,
};
// #endregion

/**
 * Which controls a row offers.
 *
 * One answer, read twice: by the row that draws them and by the column that has to be wide enough for
 * them. The conditions cannot live only inside the JSX, because a column sized from a second copy of
 * them starts drifting the moment either copy changes — and the drift is invisible, since a column
 * that is too wide still draws every button.
 */
type DocumentRowControls = {
  replace: boolean;
  remove: boolean;
  undo: boolean;
  /** Accept and Reject, which are offered together. */
  resolve: boolean;
};

function documentRowControls(
  redline: FieldRedline<WorkDocument>,
  // `disableEditing` is optional on this component's own props, and absent reads as editable.
  viewer: { canEditDocuments: boolean; disableEditing?: boolean },
): DocumentRowControls {
  const { canEditDocuments, disableEditing } = viewer;
  return {
    replace: canEditDocuments && !redline.canBeUndone,
    remove:
      canEditDocuments &&
      !redline.canBeUndone &&
      !redline.isRemoved &&
      !(redline.isAdded && !redline.isResolved),
    undo: canEditDocuments && redline.canBeUndone,
    resolve: !disableEditing && !redline.isResolved,
  };
}

/** How many of them there are, which is what the column is sized from. */
function documentRowControlCount(controls: DocumentRowControls): number {
  return (
    (controls.replace ? 1 : 0) +
    (controls.remove ? 1 : 0) +
    (controls.undo ? 1 : 0) +
    (controls.resolve ? 2 : 0)
  );
}

// #region Styles
const pulseRowOutline = keyframes`
  0%, 100% {
    border-color: rgba(21, 101, 192, 0.28);
  }
  50% {
    border-color: rgba(21, 101, 192, 0.75);
  }
`;
/**
 * The collection's box, flush to the tab's edges.
 *
 * It used to hold everything 16px off them, which left the table's first column and its right edge
 * standing away from the resize divider and the pane while the fee schedule's table met both. The
 * prose takes the inset instead — the collection's heading and the toolbar holding the search and the
 * upload control, both of which `document-collection-table` insets for the draft and active pages alike.
 * See `workPaneProseInset`.
 */
const DocumentsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  gap: theme.spacing(2),
  minWidth: "20rem",
  paddingBottom: theme.spacing(2),
}));
const DiffResult = styled("span")<{
  change?: FieldRedline<WorkDocument>;
  disabled?: boolean;
}>(({ change, disabled, theme }) => ({
  backgroundColor: (function () {
    if (disabled) return "none";
    if (change?.isAdded && !change.isResolved) return green[300];
    return "unset";
  })(),
  color: (function () {
    if (change?.isNewlyAdded) return lightBlue[700];
  })(),
  textDecoration: (function () {
    if (change?.isRemoved && !change.isResolved) return "line-through";
  })(),
  textDecorationColor: (function () {
    if (change?.isNewlyRemoved) return lightBlue[700];
    if (change?.isRemoved) return theme.palette.error.main;
    return "initial";
  })(),
}));
const ModifiedDiffResult = styled("span")<{
  change?: Change;
  resolved?: boolean;
  accepted?: boolean;
}>(({ change, resolved, accepted, theme }) => ({
  backgroundColor: (function () {
    if (resolved) return "unset";
    if (change?.added) return green[300];
  })(),
  color: (function () {
    if (change?.added && resolved) return lightBlue[700];
    return theme.palette.text.primary;
  })(),
  textDecoration: (function () {
    if (change?.removed) return "line-through";
  })(),
  textDecorationColor: (function () {
    if (accepted) return "unset";
    if (change?.removed && resolved) return lightBlue[700];
    if (change?.removed) return theme.palette.error.main;
    return "initial";
  })(),
  textDecorationThickness: '2px',
  '&:hover': {
    textDecorationColor: (function () {
      let color = "initial";
      if (change?.removed && resolved) color = lightBlue[700];
      if (change?.removed && !resolved) color = theme.palette.error.main;
      return `oklch(from ${color} l c h / 0.25)`
    }())
  },
}));
const DocumentLink = styled(ProposalInfoLink, {
  shouldForwardProp: (prop) => prop !== "disabled",
})<{ disabled?: boolean }>(({ theme, disabled }) => ({
  color: disabled ? theme.palette.text.disabled : theme.palette.info.main,
  cursor: disabled ? "default" : "pointer",
  textDecorationColor: disabled ? theme.palette.text.disabled : theme.palette.info.main,
  "&:hover": {
    color: disabled ? theme.palette.text.disabled : theme.palette.info.dark,
    textDecorationColor: disabled ? theme.palette.text.disabled : theme.palette.info.dark,
  },
  pointerEvents: disabled ? "none" : "auto",
}));
const ResolvedHelperText = styled("span")(({ theme }) => ({
  color: green[700],
  display: "block",
  fontSize: theme.typography.caption.fontSize,
  lineHeight: 1.4,
}));
/**
 * A value that a redline has added or struck out — the created stamp, the file type, the uploader.
 *
 * Wrapped INSIDE `FeeScheduleTableCellText` rather than around it: the strike-through has to land on the
 * element that directly carries the text, which is what `expectProposalDocumentStruckThrough` and the
 * component spec both read off the computed style.
 */
const DocumentRedlineValue = styled(ProposalRedlineSubtext)(() => ({
  display: "block",
  lineHeight: 1.4,
}));
const DocumentCell = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  minWidth: 0,
  width: "100%",
}));
const DocumentText = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
}));
const DocumentNameLine = styled("span", {
  shouldForwardProp: (prop) => prop !== "added",
})<{ added: boolean }>(({ added, theme }) => ({
  alignItems: "center",
  columnGap: theme.spacing(0.75),
  display: "inline-flex",
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
  paddingRight: theme.spacing(1),
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  // The document name is a Link, which pins its own font weight, so bolding the line alone would leave
  // the name unbolded.
  ...(added && {
    fontWeight: theme.typography.fontWeightBold,
    "& a": { fontWeight: "inherit" },
  }),
}));
const FileTypeIcon = styled("span")(({ theme }) => ({
  display: "inline-flex",
  flexShrink: 0,
  marginRight: theme.spacing(1),
  minWidth: "1.75rem",
  width: "1.75rem",
}));
// #endregion

// #region Types/Props
type DocumentRedlineSelectionProps = {
  className?: string;
  workDocumentType: WorkDocumentType;
  proposalId?: Guid;
  documentsRedline: FieldRedlineArray<WorkDocument>;
  downloadingFileId: Guid | null;
  accountType?: AccountType;
  negotiationId?: string;
  viewerScope?: string;
  disableEditing?: boolean;
  fieldLockTooltip?: string;
  activeReviewField: ProposalField | undefined;
  onDownload: (documentId?: Guid) => void;
  onDocumentRedlineChange: (
    redline: FieldRedlineArray<WorkDocument>,
    traversalFieldOverride?: ProposalField | null
  ) => void;
  onBeginReplaceDocument: (documentId: Guid | undefined | null) => void;
  onDocumentClicked: (id: Guid) => void;
  onTraverseToNewField: (fieldInfo: ProposalField | undefined) => void;
};
// #endregion

/**
 * One of the proposal's document collections — Conflict Documents, Client Policies or Vendor Policies —
 * drawn the way the Fee Schedule tab beside it is (#4955), so the Policies and Conflicts tabs read as
 * tables rather than as lists with a border round them.
 *
 * Four accessor columns, each carrying one thing the document HAS: Document (the file's icon and its
 * name, the way Fee Schedule puts a category's identity in its first column), Type, Added by and Added.
 * Every accessor returns the value flattened across the redline's original, revised and current entries,
 * so a search still finds a document by a spelling — or a type, or an uploader — that a redline has
 * struck out. #5027 drew all of this inside ONE column, which is what the rework is for.
 *
 * Everything the redlines themselves say is drawn here and unchanged by that: the `diffLines`
 * strike-through of a replaced document into its replacement, the added/removed colouring, the
 * decided-and-accepted surround and the active-review pulse.
 *
 * A Replace deliberately looks different from each side and that is NOT reconciled here. The party that
 * made it still holds the replaced document, tied to its replacement by `replacesId`, so they see one
 * row diffing the old name into the new; the counterparty never had the replacement and the replaced
 * document has left their collection, so they see a plain addition. Both readings are true of what each
 * party's `FieldRedlineArray` actually holds.
 *
 * Carries the Fee Schedule's whole toolbar — search, Toggle filters, Show/Hide columns, Toggle full
 * screen — and gives every data column MRT's own `⋮` column-actions menu (#4955). These had been
 * suppressed in the name of #4895, which was a misreading of it: what #4895 fixed was a Popover that
 * opened BY ITSELF, when the field-history Prev/Next traversal auto-opened a field's history and marked
 * the rest of the page `aria-hidden` with no user action at all. A menu a reader deliberately clicks
 * open is an ordinary transient dialog — the same thing as the confirm dialogs this collection already
 * raises on Remove — and it gives the page back the moment it is dismissed. The property worth
 * protecting is that NO surface here opens a modal of its own accord and blinds the page; the absence
 * of MRT's toolbar was never that property. `document-redline-selection.spec.tsx` guards the property.
 */
export default function DocumentRedlineSelection(props: Readonly<DocumentRedlineSelectionProps>) {
  const {
    className,
    workDocumentType,
    proposalId,
    documentsRedline,
    downloadingFileId,
    accountType,
    negotiationId,
    viewerScope,
    disableEditing,
    fieldLockTooltip,
    activeReviewField,
    onDownload,
    onDocumentRedlineChange,
    onBeginReplaceDocument,
    onDocumentClicked,
  } = props;

  const theme = useTheme();
  // Handed the media query as a string rather than as a `(theme) => …` callback: the callback form reads
  // the theme out of `@mui/system`'s own context, which is null wherever this table is rendered without
  // a `ThemeProvider` above it, and the row density is not worth a crash.
  const isMediumDisplaySize = useMediaQuery(theme.breakpoints.down("md"));
  const fileUploaderRef = React.useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [query, setQuery] = useState("");
  // Five columns do not fit the panel the Policies tab gives each collection, and the row-actions
  // column is pinned — so whatever does not fit scrolls underneath the controls, behind the scrollbar
  // `MRTNoMemo` styles on this container. The same container is what a double-clicked column's content
  // is measured in.
  const {
    containerRef: tableContainerRef,
    headCellProps: widthHeadCellProps,
    bodyCellProps: widthBodyCellProps,
  } = useResizableColumns<FieldRedline<WorkDocument>>();
  const showLockColumn = Boolean(disableEditing && fieldLockTooltip);

  const confirm = useConfirmDialog();
  const session = useSession();
  const [createDocuments] = useCreateDocumentsMutation();

  const collection = DOCUMENT_COLLECTIONS[workDocumentType];
  const collectionHistoryField = COLLECTION_HISTORY_FIELDS[workDocumentType];
  const rows = documentsRedline.redlines;

  /**
   * Whether the viewer OWNS this list, and so may change what is in it.
   *
   * Each party owns its own policies: the server accepts a write to `/clientPolicies/documents` only
   * from the client and `/vendorPolicies/documents` only from the vendor
   * (`ProposalCollaborationAuthorizationService.IsCounterpartyPath`). The upload control already
   * applied that rule; the row and header actions did not — so a vendor could Remove a client policy,
   * confirm a destructive dialog, watch the name strike through and see it listed in the Save
   * confirmation, and then the save failed a bare 403 that took every unrelated change in the same
   * command down with it.
   *
   * This gates OWNERSHIP only. Accept/Reject stay available on the counterparty's list, because that
   * is what negotiating somebody else's terms is: those ride as resolution decisions, which the server
   * accepts from either party (`ProposalResolutionTargetPolicy`). Conflicts documents pass no
   * `accountType` and are edited by whoever holds them, so they are unaffected.
   */
  const viewerOwnsList =
    accountType === undefined ||
    (accountType === AccountType.Vendor) === Boolean(session.context?.viewingAsVendor);
  const canEditDocuments = !disableEditing && viewerOwnsList;

  /**
   * The pinned controls column, sized to the controls THESE rows offer.
   *
   * `ACTIVE_ROW_ACTIONS_COLUMN_WIDTH` is what an active row can come to at its busiest — Replace,
   * Remove, Accept, Reject and the history dropdown — and reserving it whatever the rows hold spent
   * 192px of a 382px phone table on a collection whose one row offered Undo and a history button. The
   * widest row still sets it, so nothing wraps; it is the surface's maximum that is no longer assumed.
   */
  const rowActionsWidth = useMemo(
    () =>
      rows.reduce(
        (widest, redline) =>
          Math.max(
            widest,
            rowActionsColumnWidth(
              documentRowControlCount(documentRowControls(redline, { canEditDocuments, disableEditing })),
            ),
          ),
        rowActionsColumnWidth(0),
      ),
    [canEditDocuments, disableEditing, rows],
  );

  const [collectionRef, columnFit] = useDocumentCollectionColumnFit({
    showLockColumn,
    rowActionsWidth,
  });

  useEffect(() => {
    const target = document.getElementById(activeReviewField?.id?.value ?? "");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [activeReviewField]);

  // #region Added by
  // Who put a document on the proposal is a user id on the document, and a raw GUID is never a thing to
  // show a reader — nor to let a search match on, which is why it reaches neither the cell nor the
  // column's accessor. It is resolved through the same marketplace lookup the Fee Schedule's Team Member
  // column uses, and a document whose uploader cannot be named falls back to a label.
  const creatorUserIds = useMemo(
    () =>
      new Set(
        rows.flatMap((redline) =>
          documentEntries(redline).flatMap((entry) => (entry?.creatorId ? [entry.creatorId.value] : [])),
        ),
      ),
    [rows],
  );
  const creatorIndividualsQuery = useGetIndividualsByUserIdsQuery(
    creatorUserIds.size > 0 ? { userIds: creatorUserIds } : skipToken,
  );
  const creatorNames = useMemo(
    () =>
      new Map(
        (Individual.fromJSONArray(creatorIndividualsQuery.data, { allowEmpty: true }) ?? []).flatMap(
          (individual) => {
            const fullName = individual.fullName?.trim();
            return fullName ? [[individual.userId.value, fullName] as const] : [];
          },
        ),
      ),
    [creatorIndividualsQuery.data],
  );
  const creatorNameOf = useCallback(
    (entry: WorkDocument | null | undefined): string => {
      if (!entry?.creatorId) return UNKNOWN_VALUE;
      return creatorNames.get(entry.creatorId.value) ?? UNKNOWN_VALUE;
    },
    [creatorNames],
  );
  // #endregion

  // #region Row behaviour
  // A revert restores a document's prior value through the SAME `replaceEntryById` edit path any other
  // document change uses, so it records an ordinary redline "Edit" and flows to `queueProposalUpdate`
  // as a `CollectionItemUpdated`/`CollectionItemAdded` op — never a raw `FieldSet`.
  const handleDocumentRevert = useCallback(
    (documentRedline: FieldRedline<WorkDocument>, entry: ProposalFieldTimelineRevertEntry) => {
      if (!documentRedline.field.id) return;
      // This dropdown instance is always in collection-item mode (a document), so `entry.operation` is
      // always CollectionItemAdded/Updated at runtime — narrowed here to satisfy
      // `proposalCollectionItemRevertValue`'s parameter type, which excludes `FieldSet`.
      if (entry.operation?.kind !== "CollectionItemAdded" && entry.operation?.kind !== "CollectionItemUpdated") return;
      const revertValue = proposalCollectionItemRevertValue(entry.operation);
      if (revertValue === undefined) return;
      const revertedDocument = WorkDocument.fromJSON(revertValue as never);
      if (!revertedDocument) return;
      try {
        onDocumentRedlineChange(
          documentsRedline.replaceEntryById(documentRedline.field.id, revertedDocument),
          documentRedline.isNewlyAdded ? null : documentRedline.field,
        );
      } catch (error) {
        console.error("Failed to revert document", error);
        enqueueSnackbar("Failed to revert document", { variant: "error" });
      }
    },
    [documentsRedline, onDocumentRedlineChange],
  );

  // Takes the field id the row has already established it has, rather than re-checking it here: a row
  // with no field id offers no Remove at all, so a guard on this side could only ever be dead code.
  const handleDocumentRemoved = useCallback(
    async (fieldId: Guid) => {
      const response = await confirm({
        title: "Remove Document?",
        message: `Do you want to remove this document?`,
        okButtonText: "Remove",
      });
      if (response === ConfirmResponse.Cancel) return;
      try {
        onDocumentRedlineChange?.(documentsRedline.removeEntryByFieldId(fieldId));
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Failed to remove document", { variant: "error" });
      }
    },
    [confirm, documentsRedline, onDocumentRedlineChange],
  );

  /**
   * A click on the document's name.
   *
   * It downloads, and it also moves the review to this document's field — which the old list got for
   * free by bubbling out of the name and into the row. A table row does not fire for a click that landed
   * on one of its own controls (the name is a link), so the traversal is wired here rather than
   * inherited.
   */
  const handleDocumentNameClicked = useCallback(
    (documentRedline: FieldRedline<WorkDocument>) => {
      onDownload(downloadableDocumentId(documentRedline));
      if (disableEditing) return;
      const fieldId = documentRedline.field.id;
      if (!fieldId) return;
      onDocumentClicked(fieldId);
    },
    [disableEditing, onDocumentClicked, onDownload],
  );

  const getDiffResult = useCallback(
    (documentRedline: FieldRedline<WorkDocument>) => {
      let previousDocument: WorkDocument | null;
      let revisedDocument: WorkDocument | null;

      if (documentRedline.wasRevised && !documentRedline.isResolved) {
        previousDocument = documentRedline.originalEntry;
        revisedDocument = documentRedline.revisedEntry;
      } else if (documentRedline.isRemoved && documentRedline.isAccepted) {
        previousDocument = documentRedline.originalEntry;
        revisedDocument = null;
      } else {
        previousDocument = documentRedline.revisedEntry;
        revisedDocument = documentRedline.currentEntry ?? null;
      }

      const diff = diffLines(
        previousDocument?.name?.toString() ?? "",
        revisedDocument?.name?.toString() ?? ""
      );

      return (
        <span>
          {downloadingFileId?.isEqualTo(documentRedline.field.id) && (
            <CircularProgress
              size={16}
              thickness={4}
              color="inherit"
              disableShrink
              sx={{
                color: "text.disabled",
                marginRight: 1,
              }}
            />
          )}
          <DocumentLink
            disabled={downloadingFileId?.isEqualTo(documentRedline.field.id)}
            onClick={() => handleDocumentNameClicked(documentRedline)}
          >
            {diff.map((change: Change) => {
              return (
                <ModifiedDiffResult
                  key={JSON.stringify(change)}
                  change={change}
                  resolved={documentRedline.isResolved}
                  accepted={documentRedline.isAccepted}
                >
                  {change.value}
                </ModifiedDiffResult>
              );
            })}
          </DocumentLink>
        </span>
      );
    },
    [downloadingFileId, handleDocumentNameClicked],
  );

  const getUnchangedResult = useCallback(
    (documentRedline: FieldRedline<WorkDocument>) => (
      <span>
        {downloadingFileId?.isEqualTo(documentRedline.field.id) && (
          <CircularProgress
            size={16}
            thickness={4}
            color="inherit"
            disableShrink
            sx={{
              color: "text.disabled",
              marginRight: 1,
            }}
          />
        )}
        <DocumentLink
          disabled={downloadingFileId?.isEqualTo(documentRedline.field.id)}
          onClick={() => handleDocumentNameClicked(documentRedline)}
        >
          <DiffResult disabled={downloadingFileId?.isEqualTo(documentRedline.field.id)} change={documentRedline}>
            {documentRedline.currentEntry?.name?.value ??
              documentRedline.revisedEntry?.name?.value ??
              documentRedline.originalEntry?.name?.value}
          </DiffResult>
        </DocumentLink>
      </span>
    ),
    [downloadingFileId, handleDocumentNameClicked],
  );
  // #endregion

  // #region Cells
  const renderDocumentCell = useCallback(
    (documentRedline: FieldRedline<WorkDocument>) => {
      const displayed = displayedDocument(documentRedline);
      const extension = enumKeyOf(DocumentFileType, displayed?.fileType).toLocaleLowerCase();
      return (
        <DocumentCell>
          <FileTypeIcon>
            <FileIcon extension={extension} {...defaultStyles[extension as keyof typeof defaultStyles]} />
          </FileTypeIcon>
          <DocumentText>
            {/* `data-document-name` is how a browser test reads a collection's document names off the
                rows (`proposalDocumentNames`): a row also carries the file-type icon, whose SVG spells
                the extension out as real text, the type, the uploader, the upload stamp, an "Added"
                badge and a column of controls, so the row's own text is not the document's name. */}
            <DocumentNameLine
              data-document-name=""
              added={documentRedline.isAdded && documentRedline.isResolved}
            >
              {documentRedline.wasRevised || documentRedline.isRevised
                ? getDiffResult(documentRedline)
                : getUnchangedResult(documentRedline)}
              {documentRedline.isAdded && documentRedline.isResolved && <ProposalAddedBadge>Added</ProposalAddedBadge>}
            </DocumentNameLine>
            {isResolvedAccepted(documentRedline) && <ResolvedHelperText>Resolved</ResolvedHelperText>}
          </DocumentText>
        </DocumentCell>
      );
    },
    [getDiffResult, getUnchangedResult],
  );

  /** A value a redline may have added or struck out, on the body2 scale with the measured tooltip. */
  const renderRedlineValueCell = useCallback(
    (documentRedline: FieldRedline<WorkDocument>, value: string): ReactNode => (
      <FeeScheduleTableCellText title={value}>
        <DocumentRedlineValue
          added={documentRedline.isAdded && documentRedline.isResolved}
          removed={documentRedline.isRemoved && documentRedline.isResolved}
        >
          {value}
        </DocumentRedlineValue>
      </FeeScheduleTableCellText>
    ),
    [],
  );
  // #endregion

  // #region Columns
  const columnHelper = useMemo(() => createMRTColumnHelper<FieldRedline<WorkDocument>>(), []);
  const columns = useMemo(
    () => [
      ...(showLockColumn
        ? [
          columnHelper.display({
            header: "",
            id: LOCKED_COLUMN_ID,
            size: LOCKED_COLUMN_WIDTH,
            enableColumnActions: false,
            enableSorting: false,
            muiTableBodyCellProps: { align: "center", sx: { verticalAlign: "middle" } },
            Cell: () => (
              <DocumentLockCellContent>
                <ReviewerEditLockIcon title={fieldLockTooltip} />
              </DocumentLockCellContent>
            ),
          }),
        ]
        : []),
      columnHelper.accessor(documentNameSearchValue, {
        header: DOCUMENT_COLUMN_HEADER,
        id: DOCUMENT_COLUMN_ID,
        size: DOCUMENT_COLUMN_WIDTH,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ row }: { row: MRT_Row<FieldRedline<WorkDocument>> }) => renderDocumentCell(row.original),
      }),
      columnHelper.accessor(documentTypeSearchValue, {
        header: TYPE_COLUMN_HEADER,
        id: TYPE_COLUMN_ID,
        size: TYPE_COLUMN_WIDTH,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ row }: { row: MRT_Row<FieldRedline<WorkDocument>> }) =>
          renderRedlineValueCell(row.original, documentTypeLabel(displayedDocument(row.original))),
      }),
      columnHelper.accessor(
        (documentRedline: FieldRedline<WorkDocument>) => documentCreatorSearchValue(documentRedline, creatorNameOf),
        {
          header: ADDED_BY_COLUMN_HEADER,
          id: ADDED_BY_COLUMN_ID,
          size: ADDED_BY_COLUMN_WIDTH,
          enableGlobalFilter: true,
          filterFn: "includesString",
          Cell: ({ row }: { row: MRT_Row<FieldRedline<WorkDocument>> }) =>
            renderRedlineValueCell(row.original, creatorNameOf(displayedDocument(row.original))),
        },
      ),
      columnHelper.accessor(documentCreatedSearchValue, {
        header: ADDED_COLUMN_HEADER,
        id: ADDED_COLUMN_ID,
        size: ADDED_COLUMN_WIDTH,
        enableGlobalFilter: true,
        filterFn: "includesString",
        Cell: ({ row }: { row: MRT_Row<FieldRedline<WorkDocument>> }) =>
          renderRedlineValueCell(row.original, documentCreatedLabel(displayedDocument(row.original))),
      }),
    ],
    [columnHelper, creatorNameOf, fieldLockTooltip, renderDocumentCell, renderRedlineValueCell, showLockColumn],
  );
  // #endregion

  // #region Row actions
  const renderRowActions = useCallback(
    (actionProps: { row: MRT_Row<FieldRedline<WorkDocument>> }) => {
      const redline = actionProps.row.original;
      const controls = documentRowControls(redline, { canEditDocuments, disableEditing });
      return (
        <DocumentRowActions>
          {controls.replace && (
            <Tooltip title="Replace Document">
              <span>
                <DocumentCollectionActionButton
                  onClick={(event) => {
                    event.stopPropagation();
                    onBeginReplaceDocument(redline.field.id);
                  }}
                >
                  <UploadFileIcon color="success" />
                </DocumentCollectionActionButton>
              </span>
            </Tooltip>
          )}
          {controls.remove && (
            <Tooltip title="Remove Document">
              <span>
                <ProposalDeleteActionButton
                  aria-label="Remove Document"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (redline.currentEntry !== null && redline.field.id) {
                      void handleDocumentRemoved(redline.field.id);
                    }
                  }}
                />
              </span>
            </Tooltip>
          )}
          {controls.undo && (
            <Tooltip title="Undo Changes">
              <span>
                <DocumentCollectionActionButton
                  onClick={(event) => {
                    event.stopPropagation();
                    try {
                      if (!redline.field.id) throw new Error("Field ID is missing");
                      onDocumentRedlineChange(
                        documentsRedline.undoRedlineById(redline.field.id),
                        redline.isNewlyAdded ? null : redline.field
                      );
                    } catch (error) {
                      console.error("Failed to undo changes", error);
                      enqueueSnackbar("Failed to undo changes", {
                        variant: "error",
                      });
                    }
                  }}
                >
                  <UndoIcon color="secondary" />
                </DocumentCollectionActionButton>
              </span>
            </Tooltip>
          )}
          {/* Resolution, not ownership: accepting or rejecting the other side's proposal is exactly what
              a party does with a list it does not own, and rides as a resolution decision the server
              accepts from either party. */}
          {controls.resolve && (
            <>
              <Tooltip title={getActionButtonTooltipText(redline.isAdded, redline.isRemoved, true)}>
                <span>
                  <DocumentCollectionActionButton
                    onClick={(event) => {
                      event.stopPropagation();
                      try {
                        if (!redline.field.id) throw new Error("Field ID is missing");
                        onDocumentRedlineChange(documentsRedline.acceptRedlineById(redline.field.id));
                      } catch (error) {
                        console.error("Failed to accept changes", error);
                        enqueueSnackbar("Failed to accept changes", {
                          variant: "error",
                        });
                      }
                    }}
                  >
                    <CheckIcon color="success" />
                  </DocumentCollectionActionButton>
                </span>
              </Tooltip>
              <Tooltip title={getActionButtonTooltipText(redline.isAdded, redline.isRemoved, false)}>
                <span>
                  <DocumentCollectionActionButton
                    onClick={(event) => {
                      event.stopPropagation();
                      try {
                        if (!redline.field.id) throw new Error("Field ID is missing");
                        onDocumentRedlineChange(documentsRedline.rejectRedlineById(redline.field.id));
                      } catch (error) {
                        console.error("Failed to reject changes", error);
                        enqueueSnackbar("Failed to reject changes", {
                          variant: "error",
                        });
                      }
                    }}
                  >
                    <CloseIcon color="error" />
                  </DocumentCollectionActionButton>
                </span>
              </Tooltip>
            </>
          )}
          {/* The dropdown REVERTS, which is an ownership write; the plain button only reads. So the
              counterparty's list gets the read-only history, the same way it loses Replace and Remove. */}
          {canEditDocuments ? (
            <ProposalFieldTimelineDropdown
              field={redline.field}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
              onRevert={(entry) => handleDocumentRevert(redline, entry)}
            />
          ) : (
            <ProposalFieldHistoryButton
              field={redline.field}
              negotiationId={negotiationId}
              viewerScope={viewerScope}
            />
          )}
        </DocumentRowActions>
      );
    },
    [
      canEditDocuments,
      disableEditing,
      documentsRedline,
      handleDocumentRemoved,
      handleDocumentRevert,
      negotiationId,
      onBeginReplaceDocument,
      onDocumentRedlineChange,
      viewerScope,
    ],
  );
  // #endregion

  // #region Table plumbing
  const getRowId = useCallback(
    // The field's id, so the active-review scroll still reaches the row through `getElementById`. A
    // redline with no field id has nothing to scroll to and nothing to act on, but it still has a name
    // worth showing, so it keeps a row under a position-derived id rather than disappearing.
    (row: FieldRedline<WorkDocument>, index: number) => row.field.id?.value ?? `document-${index}`,
    [],
  );

  const muiTableBodyRowProps = useCallback(
    (rowProps: { row: MRT_Row<FieldRedline<WorkDocument>> }) => {
      const redline = rowProps.row.original;
      const fieldId = redline.field.id;
      const isActiveRow = activeReviewField?.id?.isEqualTo(fieldId) ?? false;
      const clickable = Boolean(fieldId) && !disableEditing;
      return {
        hover: false,
        id: rowProps.row.id,
        onClick: (event: ReactMouseEvent<HTMLTableRowElement>) => {
          if (!clickable || !fieldId) return;
          const target = event.target;
          // A click that landed on one of the row's own controls belongs to that control. Without this
          // the row's click handler would fire behind every button in it — and behind the name, which is
          // a link that downloads.
          if (
            target instanceof Element &&
            target.closest(
              "button, a, input, textarea, select, [role='button'], [contenteditable='true'], .MuiFormControl-root, .MuiInputBase-root",
            )
          ) return;
          onDocumentClicked(fieldId);
        },
        sx: {
          cursor: clickable ? "pointer" : "default",
          ...(isActiveRow && { animation: `${pulseRowOutline} 1.5s ease-in-out infinite` }),
        },
      };
    },
    [activeReviewField, disableEditing, onDocumentClicked],
  );

  const muiTableBodyCellProps = useCallback(
    (cellProps: {
      table: MRT_TableInstance<FieldRedline<WorkDocument>>;
      cell: { column: { id: string } };
      row: MRT_Row<FieldRedline<WorkDocument>>;
    }) => {
      const { table, cell, row } = cellProps;
      const columnId = cell.column.id;
      const isRedlinedColumn = REDLINED_COLUMN_IDS.includes(columnId);
      const isActiveRow = activeReviewField?.id?.isEqualTo(row.original.field.id) ?? false;
      // The green "decided and accepted" surround is drawn across the document's own columns and stops
      // short of the controls, exactly as the Fee Schedule's does. Which rows sit either side of this
      // one is read from the rows the table is actually showing, so a filtered-out neighbour does not
      // leave the surround open at the top or bottom.
      const showResolvedBackground = isRedlinedColumn && isResolvedAccepted(row.original);
      const visibleRows = table.getRowModel().rows;
      const visibleRowIndex = visibleRows.findIndex((visibleRow) => visibleRow.id === row.id);
      const previousRowIsResolvedAccepted = isResolvedAccepted(visibleRows[visibleRowIndex - 1]?.original);
      const nextRowIsResolvedAccepted = isResolvedAccepted(visibleRows[visibleRowIndex + 1]?.original);
      const isFirstRedlinedColumn = columnId === REDLINED_COLUMN_IDS[0];
      const isLastRedlinedColumn = columnId === REDLINED_COLUMN_IDS.at(-1);

      return {
        ...widthBodyCellProps(columnId),
        sx: {
          ...documentCollectionBodyCellStyles(theme, columnId),
          ...(showResolvedBackground && {
            backgroundColor: alpha(green[500], 0.06),
            ...(!previousRowIsResolvedAccepted && { borderTop: `1px solid ${green[500]}` }),
            ...(!nextRowIsResolvedAccepted && { borderBottom: `1px solid ${green[500]}` }),
            ...(isFirstRedlinedColumn && { borderLeft: `1px solid ${green[500]}` }),
            ...(isLastRedlinedColumn && { borderRight: `1px solid ${green[500]}` }),
          }),
          ...(isActiveRow && isRedlinedColumn && {
            borderTop: "2px solid",
            borderBottom: "2px solid",
            ...(isFirstRedlinedColumn && { borderLeft: "2px solid" }),
            ...(isLastRedlinedColumn && { borderRight: "2px solid" }),
            borderColor: "inherit",
          }),
        },
      };
    },
    [activeReviewField, theme, widthBodyCellProps],
  );

  const muiTableHeadCellProps = useCallback(
    (headProps: { column: { id: string }; table: MRT_TableInstance<FieldRedline<WorkDocument>> }) => ({
      ...widthHeadCellProps(headProps.column.id, headProps.table),
      sx: documentCollectionHeadCellStyles(theme, headProps.column.id),
    }),
    [theme, widthHeadCellProps],
  );

  const renderEmptyRowsFallback = useCallback(
    ({ table }: { table: MRT_TableInstance<FieldRedline<WorkDocument>> }) => (
      <DocumentCollectionNoRows table={table} filtered={Boolean(query)} />
    ),
    [query],
  );

  // A collection has ONE way of gaining a document — the upload — so this is a single button rather
  // than a split one. It is offered greyed out until the proposal has an id, because that is when the
  // server will take the file rather than whether the reader may give it one.
  const addActions = useMemo(
    () =>
      canEditDocuments
        ? [
          {
            label: collection.uploadLabel,
            icon: <UploadIcon />,
            loading: isUploading,
            disabled: !proposalId || proposalId.isEmpty,
            onClick: () => fileUploaderRef.current?.click(),
          },
        ]
        : [],
    [canEditDocuments, collection.uploadLabel, isUploading, proposalId],
  );

  const renderTopToolbarCustomActions = useCallback(
    ({ table }: { table: MRT_TableInstance<FieldRedline<WorkDocument>> }) => (
      <DocumentCollectionToolbar table={table} addActions={addActions} />
    ),
    [addActions],
  );

  // An empty fragment rather than `null`: MRT reads this slot's result with `??`, so a nullish return
  // falls straight back to its own default buttons and the collection would carry two sets.
  const renderToolbarInternalActions = useCallback(() => <></>, []);

  // Everything both document collections ask MRT for comes from the shared shape; what is added here is
  // this page's own rows, cells and row controls.
  const tableOptions: MRT_TableOptions<FieldRedline<WorkDocument>> = useMemo(
    () => ({
      ...documentCollectionTableOptions<FieldRedline<WorkDocument>>({
        tableName: collection.title,
        query,
        onQueryChange: setQuery,
        compactDensity: isMediumDisplaySize,
        tableContainerRef,
        columnFit,
      }),
      columns,
      data: rows as FieldRedline<WorkDocument>[],
      getRowId,
      muiTableBodyRowProps,
      muiTableBodyCellProps,
      muiTableHeadCellProps,
      renderEmptyRowsFallback,
      renderRowActions,
      renderTopToolbarCustomActions,
      renderToolbarInternalActions,
    }),
    [
      collection.title,
      columnFit,
      columns,
      getRowId,
      isMediumDisplaySize,
      muiTableBodyCellProps,
      muiTableBodyRowProps,
      muiTableHeadCellProps,
      query,
      renderEmptyRowsFallback,
      renderRowActions,
      renderToolbarInternalActions,
      renderTopToolbarCustomActions,
      rows,
      tableContainerRef,
    ],
  );
  // #endregion

  async function handleFileUpload(event: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    if (!event.currentTarget.files) {
      return;
    }
    const files = Array.from(event.currentTarget.files);
    if (files.length === 0) return;

    try {
      setIsUploading(true);
      if (!proposalId || proposalId.isEmpty) {
        throw new Error("Proposal must be saved before uploading documents.");
      }

      let context: string | undefined;
      let uploadedDocumentType: WorkDocumentType | undefined;
      switch (documentsRedline.field) {
        case ProposalField.ClientPolicies:
          context = "Policy";
          uploadedDocumentType = WorkDocumentType.ClientPolicy;
          break;
        case ProposalField.VendorPolicies:
          context = "Policy";
          uploadedDocumentType = WorkDocumentType.VendorPolicy;
          break;
        case ProposalField.Conflicts:
          context = "Conflicts";
          uploadedDocumentType = WorkDocumentType.Conflicts;
          break;
      }
      if (!uploadedDocumentType) throw new Error("Work Document Type is missing");

      const requests = createBulkDocumentRequests(files, {
        topics: [
          DocumentTopic.create({ context }),
          DocumentTopic.create({ entityClass: "Proposal", entityId: proposalId }),
        ],
      });
      const results = await createDocuments({ requests }).unwrap();
      const summary = summarizeDocumentUploadResults(results, requests);

      let updatedDocumentsRedline = documentsRedline;
      for (const uploaded of summary.documents) {
        const workDocument = WorkDocument.fromDocument(uploaded, uploadedDocumentType);
        updatedDocumentsRedline = updatedDocumentsRedline.addEntry(workDocument);
      }
      if (summary.uploadedCount > 0) {
        onDocumentRedlineChange?.(updatedDocumentsRedline);
      }

      const snackbar = getDocumentUploadSnackbar(summary);
      enqueueSnackbar(snackbar.message, { variant: snackbar.variant });
    } catch (error) {
      console.error("Failed to upload file", error);

      const status = getApiErrorStatus(error);
      if (status === HttpStatusCode.UnsupportedMediaType) {
        enqueueSnackbar(
          getApiErrorMessage(getApiErrorData(error), { knownLegacyText: knownLegacyDocumentErrors })
            ?? "Unsupported file type",
          { variant: "error" },
        );
      } else if (status === HttpStatusCode.UnprocessableEntity) {
        enqueueSnackbar("Unsupported file type", { variant: "error" });
      } else {
        enqueueSnackbar(
          getErrorMessageFromUnknown(error) ?? "Unknown error occurred during upload",
          { variant: "error" },
        );
      }
    } finally {
      event.target.value = "";
      setIsUploading(false);
    }
  }

  // #4953: no per-collection Accept All / Reject All / Undo All on this collection's header. Every change
  // is decided on the row it belongs to, and the whole-proposal bulk actions on the active proposal page
  // cover "settle everything at once" — a second, collection-scoped copy of them was clutter.

  return (
    <DocumentsContainer className={className}>
      <DocumentCollectionRoot data-document-collection="" ref={collectionRef}>
        <DocumentCollectionHeader data-document-collection-header="">
          {/* The title stays a direct text child of the heading, with the info tooltip beside it rather
              than around it: a wrapper would take the words out of the heading's own text node, and the
              specs read the heading as `getByText(title, { selector: "h3" })`. */}
          <DocumentCollectionHeading>
            {collection.title}
            <ProposalInfoTooltip title={collection.about} placement="bottom" edge="end" />
          </DocumentCollectionHeading>
          {collectionHistoryField && (
            <DocumentCollectionActions>
              <ProposalFieldHistoryButton
                field={collectionHistoryField}
                negotiationId={negotiationId}
                viewerScope={viewerScope}
              />
            </DocumentCollectionActions>
          )}
        </DocumentCollectionHeader>
        <MRTNoMemo<FieldRedline<WorkDocument>> options={tableOptions} />

      </DocumentCollectionRoot>
      <input type="file" hidden={true} multiple={true} ref={fileUploaderRef} onChange={handleFileUpload} />
    </DocumentsContainer>
  );
}

// #region Row values
// Module-level, so the compiler leaves them alone and so each column's accessor is one named thing
// rather than an inline lambda whose meaning has to be reconstructed from its body.

/** Every version of the document this row stands for, newest reading first. */
function documentEntries(documentRedline: FieldRedline<WorkDocument>): (WorkDocument | null | undefined)[] {
  return [documentRedline.currentEntry, documentRedline.revisedEntry, documentRedline.originalEntry];
}

/**
 * The document a row's cells describe: where its decisions leave it, or — for a removal — the document
 * that is being removed, which is still what the reader is being asked about.
 */
function displayedDocument(documentRedline: FieldRedline<WorkDocument>): WorkDocument | null | undefined {
  return documentRedline.currentEntry ?? documentRedline.revisedEntry ?? documentRedline.originalEntry;
}

/** Whether this row's change has been decided, and decided in favour of the change. */
function isResolvedAccepted(documentRedline: FieldRedline<WorkDocument> | undefined): boolean {
  return Boolean(
    documentRedline?.wasRevised &&
    documentRedline.isResolved &&
    documentRedline.isAccepted &&
    !documentRedline.isRemoved,
  );
}

function distinctSearchValue(values: (string | undefined)[]): string {
  return [...new Set(values.filter((value): value is string => Boolean(value)))].join(" ");
}

/**
 * Every spelling this document has carried, flattened, so a search over the collection still finds a
 * document by a name a redline has struck out.
 */
function documentNameSearchValue(documentRedline: FieldRedline<WorkDocument>): string {
  return distinctSearchValue(documentEntries(documentRedline).map((entry) => entry?.name?.value));
}

function documentTypeSearchValue(documentRedline: FieldRedline<WorkDocument>): string {
  return distinctSearchValue(
    documentEntries(documentRedline).map((entry) => (entry ? documentTypeLabel(entry) : undefined)),
  );
}

function documentCreatedSearchValue(documentRedline: FieldRedline<WorkDocument>): string {
  return distinctSearchValue(
    documentEntries(documentRedline).map((entry) => (entry ? documentCreatedLabel(entry) : undefined)),
  );
}

/**
 * What a row's Accept or Reject is named for.
 *
 * The verb used to carry its own trailing space AND the templates added one, so every one of these read
 * "Accept  New Document" with a doubled space — in the tooltip a user sees and in the accessible name a
 * screen reader announces. Invisible to the component tests because Testing Library normalizes
 * whitespace in `getByLabelText`; an exact attribute match, which is what `e2e/support/proposal-ui.ts`
 * uses, does not.
 */
function getActionButtonTooltipText(isAdded: boolean, isRemoved: boolean, accept: boolean): string {
  const verb = accept ? "Accept" : "Reject";

  if (isAdded) return `${verb} New Document`;
  if (isRemoved) return `${verb} Removed Document`;
  return `${verb} All Changes`;
}

/**
 * The uploaders this row has had, by name.
 *
 * A replacement is frequently uploaded by the OTHER party, so the row can honestly carry two — and a
 * search for either must find it. What never reaches this string is the `creatorId` itself: it is a raw
 * GUID, and a GUID is neither a thing to show a reader nor a thing to let them match on by accident.
 */
function documentCreatorSearchValue(
  documentRedline: FieldRedline<WorkDocument>,
  creatorNameOf: (entry: WorkDocument | null | undefined) => string,
): string {
  return distinctSearchValue(documentEntries(documentRedline).map((entry) => (entry ? creatorNameOf(entry) : undefined)));
}

function downloadableDocumentId(documentRedline: FieldRedline<WorkDocument>): Guid | undefined {
  return (
    documentRedline.currentEntry?.id ??
    documentRedline.revisedEntry?.id ??
    documentRedline.originalEntry?.id ??
    undefined
  );
}
// #endregion
